import { Component, OnDestroy, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { Resource } from '../../models/resources/resource.model';
import { ResourcesService } from '../../services/resources.service';
import { ToastsService } from '../../services/toasts.service';
import { Base } from '../base/base';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { User } from '../../models/user/user.model';
import { EventTrackingService } from 'src/app/services/event-tracking.service';
import { EventName } from '../../../environments/environment';

@Component({
  selector: 'safetydirector-favorite-resources',
  templateUrl: './favorite-resources.component.html',
  styleUrls: ['./favorite-resources.component.css'],
  providers: [ResourcesService]
})
export class FavoriteResourcesComponent extends Base implements OnInit, OnDestroy {
  private BusyA;
  private BusyB;
  private BusyC;

  public selectedIndustry: string;

  public resourcesCopy: Resource[] | any;
  public resources: Resource[] | any;
  public baseUrl: string;

  public searchString: string | null = null;

  public selectedVideo: Resource;
  public selectedVideoUrl: SafeResourceUrl;

  public pageName = 'Favorite Resources';

  public localStorageKey = 'memic-favorite-resources';

  public tagsLength = 4;

  constructor(
    private resourcesService: ResourcesService,
    private sanitized: DomSanitizer,
    private toastsService: ToastsService,
    private modalService: NgbModal,
    private titleService: Title,
    private eventTrackingService: EventTrackingService) {
    super();
    this.titleService.setTitle(this.basePageTitle + this.pageName);
  }

  ngOnInit(): void {
    this.selectedIndustry = "All";

    this.initializeResources();
  }

  public initializeResources() {
    this.resourcesCopy = null;
    this.resources = null;

    let favorites = JSON.parse(localStorage.getItem(this.localStorageKey)!);

    this.BusyA = this
      .resourcesService
      .getAllResources(this.selectedIndustry)
      .subscribe(result => {
        _.forEach(result, function (r) {
          r.favorite = _.indexOf(favorites, r.id) >= 0;
        });

        result = _.filter(result, function (r) {
          return (r.favorite);
        });

        this.resourcesCopy = result;
        this.resources = result;

        if (this.searchString) {
          this.filterResources();
        }
      });
  }

  public filterResources(clearSearch = false) {
    this.resources = this.resourcesCopy;

    if (clearSearch) {
      this.searchString = null;
    }

    // search string
    if (this.searchString) {
      let ss = this.searchString.toLowerCase();
      this.resources = _.filter(this.resources, function (r) {
        return (r.title && r.title.toLowerCase().includes(ss)) ||
          (r.resourceDescription && r.resourceDescription.toLowerCase().includes(ss)) ||
          (r.documentType && r.documentType.toLowerCase().includes(ss)) ||
          (r.primaryFeatureTopic && r.primaryFeatureTopic.toLowerCase().includes(ss)) ||
          (r.secondaryFeaturedTopic && r.secondaryFeaturedTopic.join('|#|~~|#|').toLowerCase().includes(ss));
      });
    }
  }

  public downloadFile(file, fileIcon) {
    let fileName = file.blobName;
    let user: User = this.getUserDetailsFromLocalStorage();
    // Replace the file icon with a spinner, save the original icon to replace when download is complete
    let originalFileIconSrc = fileIcon.src;
    fileIcon.src = "../../assets/images/spinner.gif"

    this.BusyC = this
      .resourcesService
      .downloadFile(file, this.getIndustry(), user.policyNumber, user.emailAddress)
      .subscribe(result => {
        const a = document.createElement('a')
        const objectUrl = URL.createObjectURL(result)
        a.href = objectUrl
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(objectUrl);
        // Put back the original icon
        fileIcon.src = originalFileIconSrc;
        //track event
        this.eventTrackingService.trackEvent('TrackEvent', {
          'Event': EventName.favoriteResourcesDownloadFile
         ,'FileName': fileName
        });

        this.toastsService.showSuccess(`Successfully downloaded '${fileName}'`);
      });
  }

  private getKeyWordsHtmlHelper(keyword: string): string {
    return keyword ? `<section class="memic-tag m-r-1 m-t-1" > <i class="fa fa-tag p-l-1 m-r-1" aria-hidden="true" > </i><span class="p-r-1">${keyword}</span > </section>` : '';
  }

  public getKeyWordsHtml(resource: Resource, firstFew: boolean = true): string {
    let keyWordsHtml = '';
    let start, end = 0;

    let keywords = this.getKeyWords(resource);

    if (firstFew) {
      start = 0;
      end = this.tagsLength;
    } else {
      start = this.tagsLength;
      end = keywords?.length;
    }

    _.each(_.slice(keywords, start, end), k => keyWordsHtml += this.getKeyWordsHtmlHelper(k));

    return keyWordsHtml;
  }

  public getKeyWords(resource: Resource): string[] {
    let keyWords: string[] = [];

    // Add Document Types
    if (resource.documentType) {
      keyWords.push(resource.documentType);
    }

    // Add Featured Topic
    if (resource.primaryFeatureTopic) {
      keyWords.push(resource.primaryFeatureTopic);
    }

    //Add Secondary Featured Topics
    if (resource.secondaryFeaturedTopic && resource.secondaryFeaturedTopic.length > 0) {
      for (let i = 0; i < resource.secondaryFeaturedTopic.length; i++) {
        keyWords.push(resource.secondaryFeaturedTopic[i]);
      }
    }

    return keyWords;
  }

  // Show more of the tags
  public toggleTags(resource: Resource) {
    if (resource.more) {
      resource.more = false
    } else {
      resource.more = true;
    }
  }

  // Add/ Remove Favorites in local storage
  public toggleFavorite(resource: Resource) {
    let favorites = JSON.parse(localStorage.getItem(this.localStorageKey)!);
    let favoritesSet = new Set(favorites);
    
    if (resource.favorite) {
      favoritesSet.delete(resource.id);
      resource.favorite = false
      //track event
      this.eventTrackingService.trackEvent('TrackEvent', {
        'Event': EventName.favoriteResourcesRemove
       ,'ResourceName': resource.blobName
      });
    } else {
      favoritesSet.add(resource.id);
      resource.favorite = true;
      //track event
      this.eventTrackingService.trackEvent('TrackEvent', {
        'Event': EventName.favoriteResourcesAdd
       ,'ResourceName': resource.blobName
      });
    }

    localStorage.setItem(this.localStorageKey, JSON.stringify(Array.from(favoritesSet)));
  }

  public getFavoritesCount() {
    let favorites = JSON.parse(localStorage.getItem(this.localStorageKey)!);

    return favorites != null ? favorites.length : 0;
  }

  openModal(targetModal, video) {
    const modalRef = this.modalService.open(targetModal, {
      centered: true,
      backdrop: 'static',
      size: 'xl'
    });

    this.selectedVideo = video;
    this.selectedVideoUrl = this.sanitized.bypassSecurityTrustResourceUrl(video.blobUrl);
  }

  ngOnDestroy(): void {
    if (this.BusyA) {
      this.BusyA.unsubscribe();
    }

    if (this.BusyB) {
      this.BusyB.unsubscribe();
    }

    if (this.BusyC) {
      this.BusyC.unsubscribe();
    }
  }
}
